/**
 * #5068 — declared motion (D4). One master clock turns once per `period`; every joint follows it at its `ratio`. The
 * result is, per part, an offset and a turn (and, for springs, links, wires and arrows, moved end points). Room, TV and
 * server evaluate the same function at the same clock, so motion agrees everywhere. No model-written code runs here.
 *
 * Joints are resolved in dependency order, never in the order a program declared them: first the driving joints
 * (rotate, slide, crank–slider, cam), then gears (repeated so a chain of gears settles), then rods following their
 * crank and slider, then attached ends. Crank and cam geometry comes from the drawing itself when not given, so a model
 * does not have to hand-match a crank's throw, a rod's length or a cam's timing.
 */
import type { ContextualVisualDrawing, DrawingJoint, DrawingPart } from './DrawingSchema';
import { isAbsoluteDrawingKit, placeDrawingPart } from './drawingParts';
import type { Point } from './sketchKit';

export interface PartMotion { offset?: Point; turn?: number; ends?: { from?: Point; to?: Point } }
const TAU = Math.PI * 2;
const rad = (degrees: number) => degrees * Math.PI / 180;

/** Where a crank-driven slider sits along its axis, measured from its top-dead-centre position (0 at angle 0). */
export function crankSliderTravel(angle: number, radius: number, rodLength: number): number {
  const reach = (a: number) => radius * Math.cos(a) + Math.sqrt(Math.max(0, rodLength ** 2 - (radius * Math.sin(a)) ** 2));
  return reach(0) - reach(angle);
}
/** Follower lift for a cam at `angle` (radians): a smooth bump of `width` (fraction of a turn) centred on `center`. */
export function camLift(angle: number, lift: number, center: number, width: number): number {
  const turn = ((angle / TAU - center) % 1 + 1.5) % 1 - .5;
  return Math.abs(turn) >= width / 2 ? 0 : lift * Math.cos(turn / width * Math.PI) ** 2;
}

/**
 * The crank's geometry as drawn: its centre, the pin's distance and drawn direction, and the rod length from the pin at
 * top dead centre (pointing along `axis`) to the slider's wrist as placed.
 */
function crankGeometry(crank: DrawingPart, slider: DrawingPart, axis: number) {
  const centre: Point = [crank.x, crank.y], pin = placeDrawingPart(crank)?.anchors.pin ?? centre;
  const wrist = placeDrawingPart(slider)?.anchors.pin ?? [slider.x, slider.y];
  const radius = Math.hypot(pin[0] - centre[0], pin[1] - centre[1]);
  const top: Point = [centre[0] + Math.cos(axis) * radius, centre[1] + Math.sin(axis) * radius];
  return { centre, radius, pinAngle: Math.atan2(pin[1] - centre[1], pin[0] - centre[0]), rodLength: Math.hypot(wrist[0] - top[0], wrist[1] - top[1]) };
}

/** The fraction of a turn at which a cam's lobe (drawn pointing up, then rotated by the part) points along `axis`. */
export function camPeakTurn(cam: Pick<DrawingPart, 'rotate'>, axisDegrees: number): number {
  return (((axisDegrees - (cam.rotate - 90)) / 360) % 1 + 1) % 1;
}

/** Every moving part's motion at master angle `angle` (radians; 0 = the drawing as placed). */
export function drawingMotionAt(drawing: Pick<ContextualVisualDrawing, 'parts' | 'joints'>, angle: number): Map<string, PartMotion> {
  const motion = new Map<string, PartMotion>();
  const parts = new Map(drawing.parts.map(part => [part.id, part]));
  const set = (id: string, change: PartMotion) => motion.set(id, { ...motion.get(id), ...change });
  // Turning means nothing for a part drawn from sheet points (an arrow, a rod): it can only move.
  const turn = (id: string, value: number) => { if (!isAbsoluteDrawingKit(parts.get(id)?.kit ?? '')) set(id, { turn: value }); };
  const ofKind = <K extends DrawingJoint['kind']>(kind: K) => drawing.joints.filter((joint): joint is Extract<DrawingJoint, { kind: K }> => joint.kind === kind);
  const links: Array<() => void> = [];

  for (const joint of drawing.joints) {
    if (joint.kind === 'rotate') turn(joint.part, joint.ratio * angle + joint.phase * TAU);
    else if (joint.kind === 'slide') {
      const k = Math.sin(joint.ratio * angle + joint.phase * TAU);
      set(joint.part, { offset: [joint.dx * k, joint.dy * k] });
    } else if (joint.kind === 'crankSlider') {
      const crank = parts.get(joint.crank), slider = parts.get(joint.slider);
      if (!crank || !slider) continue;
      const axis = rad(joint.axis), geometry = crankGeometry(crank, slider, axis);
      const radius = joint.radius ?? geometry.radius, rodLength = Math.max(radius + 1, joint.rodLength ?? geometry.rodLength);
      const a = joint.ratio * angle + joint.phase * TAU;
      // At a = 0 the pin points at the slider (top dead centre), whatever way the crank was drawn.
      turn(joint.crank, a + axis - geometry.pinAngle);
      const travel = crankSliderTravel(a, radius, rodLength);
      // Positive travel moves the slider back along its axis, toward the crank.
      set(joint.slider, { offset: [-Math.cos(axis) * travel, -Math.sin(axis) * travel] });
      if (joint.link) {
        const link = joint.link;
        links.push(() => {
          const pin: Point = [geometry.centre[0] + Math.cos(axis + a) * radius, geometry.centre[1] + Math.sin(axis + a) * radius];
          const wrist = placeDrawingPart(slider, motion.get(slider.id))?.anchors.pin ?? [slider.x, slider.y];
          set(link, { ends: { from: pin, to: wrist } });
        });
      }
    } else if (joint.kind === 'cam') {
      const cam = parts.get(joint.cam);
      if (!cam || !parts.has(joint.follower)) continue;
      const a = joint.ratio * angle + joint.phase * TAU, axis = rad(joint.axis);
      const lift = camLift(a, joint.lift, joint.center ?? camPeakTurn(cam, joint.axis), joint.width);
      turn(joint.cam, a);
      set(joint.follower, { offset: [Math.cos(axis) * lift, Math.sin(axis) * lift] });
    }
  }
  // Gears follow their drivers, through chains: settle in as many passes as there are gears.
  const gears = ofKind('gear');
  for (let pass = 0; pass < gears.length; pass++) {
    for (const gear of gears) turn(gear.driven, -gear.ratio * (motion.get(gear.driver)?.turn ?? 0));
  }
  for (const link of links) link();
  for (const joint of ofKind('attach')) {
    const part = parts.get(joint.part), follow = motion.get(joint.follow)?.offset;
    const end = part?.params?.[joint.end];
    if (!part || !follow || !Array.isArray(end) || end.length !== 2 || typeof end[0] !== 'number' || typeof end[1] !== 'number') continue;
    set(joint.part, { ends: { ...motion.get(joint.part)?.ends, [joint.end]: [end[0] + follow[0], end[1] + follow[1]] } });
  }
  return motion;
}
